import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { type equipment as PrismaEquipment, type practitioner as PrismaPractitioner } from "@server/prisma-generated/client";
import { keepPreviousData } from "@tanstack/react-query";
import dayjs from 'dayjs';
import { AlertTriangle, Book, Check, ChevronsUpDown, Loader2, Terminal } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Switch } from '~/components/ui/switch';
import { useDebounce } from '~/hooks/use-debounce';
import { useDebounceFunction } from '~/hooks/use-debounce-function';
import LocalDateTime, { IANATimezone } from '~/lib/localdatetime';
import { serializeConflictsForLog } from '~/lib/schedule-entry-conflicts';
import { cn } from '~/lib/utils';
import { LogsTable } from "~/pages/Logs";
import { useUserStore } from '~/stores/user-store';
import { EquipmentScheduleEntryType } from '~/types/server-types/EquipmentScheduleEntryType';
import { PractitionerScheduleEntryBlockedType } from '~/types/server-types/PractitionerScheduleEntryBlockedType';
import { PractitionerScheduleEntryType } from '~/types/server-types/PractitionerScheduleEntryType';
import { UserRole } from '~/types/server-types/UserRole';
import { RecurrenceEditModeDialog, type RecurrenceEditMode } from './RecurrenceEditModeDialog';

const formatDate = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('yyyy-MM-dd') : '';
};

const formatTime = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('HH:mm') : '';
};

const parseDate = (dateStr: string, existingTime: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!dateStr) return null;
    const time = existingTime ? existingTime.format('HH:mm') : '00:00';
    return new LocalDateTime(`${dateStr} ${time}`, timezone);
};

const parseTime = (timeStr: string, existingDate: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!timeStr || !existingDate) return null;
    const date = existingDate.format('yyyy-MM-dd');
    return new LocalDateTime(`${date} ${timeStr}`, timezone);
};

type ScheduleEntryLogsModalProps = {
    scheduleEntryId: string;
    open: boolean;
    onClose: () => void;
};

function ScheduleEntryLogsModal({ scheduleEntryId, open, onClose }: ScheduleEntryLogsModalProps) {
    const logsQuery = trpc.logs.getLogs.useQuery(
        {
            page: 1,
            pageSize: 100,
            searchQuery: scheduleEntryId,
        },
        {
            placeholderData: keepPreviousData,
            enabled: open === true,
        }
    );

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Blockierungs-Logs</DialogTitle>
                    <DialogDescription>
                        Alle System-Logs für diese Blockierung (ID: <span className="font-mono">{scheduleEntryId}</span>)
                    </DialogDescription>
                </DialogHeader>
                <div className="p-4">
                    {logsQuery.isError && (
                        <Alert variant="destructive" className="mb-6">
                            <Terminal className="h-4 w-4" />
                            <AlertTitle>Fehler beim Laden der Logs</AlertTitle>
                            <AlertDescription>
                                {logsQuery.error?.message ?? "Ein unbekannter Fehler ist aufgetreten."}
                            </AlertDescription>
                        </Alert>
                    )}
                    <div className="rounded-md border bg-muted/50 p-2 max-h-[50vh] overflow-auto">
                        <LogsTable
                            logs={logsQuery.data?.logs ?? []}
                            isLoading={logsQuery.isLoading}
                            itemsPerPage={100}
                        />
                        {logsQuery.data?.logs?.length === 0 && !logsQuery.isLoading && (
                            <div className="text-center text-muted-foreground py-8">
                                Keine Logs für diese Blockierung gefunden.
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Schließen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

type Practitioner = Pick<PrismaPractitioner, 'id' | 'firstName' | 'lastName' | 'title'>;
type Equipment = Pick<PrismaEquipment, 'id' | 'name'>

interface ScheduleEntryFormProps {
    dataObjectId: number; // Practitioner ID or Equipment ID
    dataObjectType: 'practitioner' | 'equipment';
    storeId: number;
    userTimezone: string; // Pass user's timezone
    scheduleEntryId?: string; // For modifying an existing schedule entry, this is the ID of the entry.
    overwriteEntryType?: PractitionerScheduleEntryType | EquipmentScheduleEntryType;
    selectedSlot?: { start: LocalDateTime, end: LocalDateTime }; // Optional prefill from calendar click
    onSuccess: () => void; // To close dialog and refresh
    onCancel: () => void; // To close dialog
}

export const ScheduleEntryForm: React.FC<ScheduleEntryFormProps> = ({
    dataObjectId,
    dataObjectType,
    scheduleEntryId,
    storeId,
    userTimezone,
    overwriteEntryType,
    selectedSlot,
    onSuccess,
    onCancel
}) => {
    console.log("selectedSlot");
    console.log(selectedSlot);
    const isEditing = !!scheduleEntryId;
    const userRole = useUserStore(state => state.user?.role);
    const canManageShiftEntries = userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR;
    const utils = trpc.useUtils();

    const [onlyUpdateForOriginalPractitioner, setOnlyUpdateForOriginalPractitioner] = useState(false);

    const [originalSeriesStart, setOriginalSeriesStart] = useState<LocalDateTime | null>(null);
    const [originalSeriesEnd, setOriginalSeriesEnd] = useState<LocalDateTime | null>(null);

    const originalPractitionerData = trpc.practitioners.getOne.useQuery({
        id: dataObjectId
    });
    const originalEquipmentData = trpc.equipment.getOne.useQuery({
        id: dataObjectId
    });

    const practitionerScheduleEntryQuery = trpc.practitioners.getScheduleEntry.useQuery({
        id: scheduleEntryId ?? '--'
    }, {
        enabled: !!scheduleEntryId && dataObjectType === 'practitioner',
    });
    const equipmentScheduleEntryQuery = trpc.equipment.getScheduleEntry.useQuery({
        id: scheduleEntryId ?? '--'
    }, {
        enabled: !!scheduleEntryId && dataObjectType === 'equipment',
    });

    const existingScheduleEntryData: typeof practitionerScheduleEntryQuery.data | typeof equipmentScheduleEntryQuery.data | null = useMemo(() => {
        if (!isEditing) {
            return null;
        }
        if (dataObjectType === 'practitioner') {
            return practitionerScheduleEntryQuery.data ?? null;
        } else if (dataObjectType === 'equipment') {
            return equipmentScheduleEntryQuery.data ?? null;
        }
    }, [isEditing, dataObjectType, practitionerScheduleEntryQuery, equipmentScheduleEntryQuery]);

    const dataObjectDisplayNames = useMemo(() => {
        if (!existingScheduleEntryData) {
            if (!isEditing && originalPractitionerData.data) {
                const practitioner = originalPractitionerData.data;
                const displayName = `${!!practitioner.title && practitioner.title.length > 0 ? `${practitioner.title} ` : ''} ${practitioner.firstName} ${practitioner.lastName}`
                return displayName;
            }
            return null;
        }
        if (dataObjectType === 'practitioner' && 'practitionerScheduleEntryGroup' in existingScheduleEntryData) {
            const group = existingScheduleEntryData.practitionerScheduleEntryGroup;
            if (!!group && group.practitionerScheduleEntries.length > 0) {
                // If the schedule entry is in a group, ALL practitioners will be affected who have schedule entries in the same group.
                return group.practitionerScheduleEntries.map((entry) => {
                    const practitioner = entry.practitioner;
                    const displayName = `${!!practitioner.title && practitioner.title.length > 0 ? `${practitioner.title} ` : ''} ${practitioner.firstName} ${practitioner.lastName}`
                    return displayName;
                }).join(", ");
            } else {
                const practitioner = existingScheduleEntryData.practitioner;
                const displayName = `${!!practitioner.title && practitioner.title.length > 0 ? `${practitioner.title} ` : ''} ${practitioner.firstName} ${practitioner.lastName}`
                return displayName;
            }
        } else if (dataObjectType === 'equipment' && 'equipmentScheduleEntryGroup' in existingScheduleEntryData) {
            const group = existingScheduleEntryData.equipmentScheduleEntryGroup;
            if (!!group && group.equipmentScheduleEntries.length > 0) {
                // If the schedule entry is in a group, ALL equipments will be affected who have schedule entries in the same group.
                return group.equipmentScheduleEntries.map((entry) => {
                    const equipment = entry.equipment;
                    return equipment.name;
                }).join(", ");
            } else {
                return existingScheduleEntryData.equipment.name;
            }
        }
    }, [existingScheduleEntryData]);

    const isScheduleEntryInGroup = useMemo(() => {
        if (!existingScheduleEntryData) {
            return false;
        }
        if (dataObjectType === 'practitioner') {
            return 'practitionerScheduleEntryGroup' in existingScheduleEntryData && !!existingScheduleEntryData.practitionerScheduleEntryGroup;
        } else if (dataObjectType === 'equipment') {
            return 'equipmentScheduleEntryGroup' in existingScheduleEntryData && !!existingScheduleEntryData.equipmentScheduleEntryGroup;
        }
        return false;
    }, [existingScheduleEntryData]);

    const [type, setType] = useState<PractitionerScheduleEntryType | EquipmentScheduleEntryType>(
        overwriteEntryType ? overwriteEntryType
            : dataObjectType === 'practitioner' ? PractitionerScheduleEntryType.AVAILABLE
                : EquipmentScheduleEntryType.AVAILABLE
    );
    const [blockedType, setBlockedType] = useState<PractitionerScheduleEntryBlockedType | null>(null);
    const [description, setDescription] = useState<string>('');

    const [startTimeObj, setStartTimeObj] = useState<LocalDateTime | null>(() => {
        // if (initialData?.startTime) return dayjs(initialData.startTime).tz(userTimezone);
        if (selectedSlot?.start) return selectedSlot.start;
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(1, 'hour').utc().toDate(), 'Europe/Berlin');
    });
    const [endTimeObj, setEndTimeObj] = useState<LocalDateTime | null>(() => {
        // if (initialData?.endTime) return dayjs(initialData.endTime).tz(userTimezone);
        if (selectedSlot?.end) return selectedSlot.end;
        if (startTimeObj) return startTimeObj.clone().addMinutes(60);
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(2, 'hour').utc().toDate(), 'Europe/Berlin');
    });

    const [isRecurring, setIsRecurring] = useState(false);
    const [recurrenceEndDateObj, setRecurrenceEndDateObj] = useState<dayjs.Dayjs | null>(dayjs().endOf('year'));

    const [frequency, setFrequency] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY'); // Default
    const [interval, setInterval] = useState<number>(1); // Default: Every 1 day/week/month
    const [weekdays, setWeekdays] = useState<Record<string, boolean>>({ // For weekly selection
        MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false,
    });

    useEffect(() => {
        if (!isEditing || !existingScheduleEntryData) {
            return;
        }
        // Initialize data from existing schedule entry
        setType(existingScheduleEntryData.type);
        if ('blockedType' in existingScheduleEntryData) {
            setBlockedType(existingScheduleEntryData.blockedType);
        }
        setDescription(existingScheduleEntryData.description || '');

        const seriesStart = LocalDateTime.deserialize(existingScheduleEntryData.startTimeLocal);
        const seriesEnd = LocalDateTime.deserialize(existingScheduleEntryData.endTimeLocal);
        setOriginalSeriesStart(seriesStart);
        setOriginalSeriesEnd(seriesEnd);

        if (existingScheduleEntryData.isRecurring && selectedSlot) {
            setStartTimeObj(selectedSlot.start);
            setEndTimeObj(selectedSlot.end);
        } else {
            setStartTimeObj(seriesStart);
            setEndTimeObj(seriesEnd);
        }

        if (existingScheduleEntryData.recurrenceEndDate) {
            setRecurrenceEndDateObj(dayjs(existingScheduleEntryData.recurrenceEndDate).tz(userTimezone));
        } else if (existingScheduleEntryData.isRecurring) {
            setRecurrenceEndDateObj(null);
        }
    }, [existingScheduleEntryData]);

    // *** We will now DERIVE the recurrenceRule using useEffect ***
    const [derivedRecurrenceRule, setDerivedRecurrenceRule] = useState<string>('');

    // Update end time if start time changes and it was the default duration
    useEffect(() => {
        if (startTimeObj && !existingScheduleEntryData?.endTime && !selectedSlot?.end) {
            setEndTimeObj(startTimeObj.clone().addMinutes(60));
        }
    }, [startTimeObj, existingScheduleEntryData?.endTime, selectedSlot?.end]);


    useEffect(() => {
        if (existingScheduleEntryData?.isRecurring && existingScheduleEntryData.recurrenceRule) {
            try {
                // Basic parsing for FREQ, INTERVAL, BYDAY (add more if needed)
                const ruleParts = existingScheduleEntryData.recurrenceRule.split(';');
                let initialFreq: 'DAILY' | 'WEEKLY' | 'MONTHLY' = 'WEEKLY'; // Default fallback
                let initialInterval = 1;
                const initialWeekdays: Record<string, boolean> = { MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false };

                ruleParts.forEach(part => {
                    const [key, value] = part.split('=');
                    if (key === 'FREQ' && (value === 'DAILY' || value === 'WEEKLY' || value === 'MONTHLY')) {
                        initialFreq = value;
                    } else if (key === 'INTERVAL') {
                        initialInterval = parseInt(value, 10) || 1;
                    } else if (key === 'BYDAY' && value) {
                        value.split(',').forEach(day => {
                            if (day in initialWeekdays) {
                                initialWeekdays[day] = true;
                            }
                        });
                    }
                });

                setFrequency(initialFreq);
                setInterval(initialInterval);
                setWeekdays(initialWeekdays);
                // Also set the main toggle
                setIsRecurring(true);

            } catch (e) {
                console.error("Could not parse initial RRULE:", existingScheduleEntryData.recurrenceRule, e);
                // Fallback: Keep isRecurring false or show raw input? For now, we assume simple rules.
                setIsRecurring(false); // Safer fallback if parsing fails
            }
        } else if (!existingScheduleEntryData?.id) {
            // Reset builder state if creating a new entry (or if existingScheduleEntryData is cleared)
            // Set default based on start date when isRecurring is first checked (see next useEffect)
            setIsRecurring(existingScheduleEntryData?.isRecurring ?? false); // Respect existingScheduleEntryData if provided but no rule
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingScheduleEntryData]); // Run only when existingScheduleEntryData changes

    const dayjsToRruleWeekdays: { [key: number]: string } = {
        0: 'SU', 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA'
    };

    useEffect(() => {
        if (!isRecurring) {
            setDerivedRecurrenceRule(''); // Clear rule if not recurring
            // Optionally reset builder state here too for cleanliness
            // setFrequency('WEEKLY');
            // setInterval(1);
            // setWeekdays({ MO: false, ... });
            return;
        }

        let parts: string[] = [];
        parts.push(`FREQ=${frequency}`);

        if (interval > 1) {
            parts.push(`INTERVAL=${interval}`);
        }

        if (frequency === 'WEEKLY') {
            const selectedDays = Object.entries(weekdays)
                .filter(([, isSelected]) => isSelected)
                .map(([day]) => day);

            if (selectedDays.length > 0) {
                parts.push(`BYDAY=${selectedDays.join(',')}`);
            } else {
                // Handle case where no days are selected - maybe default to start day?
                // Or rely on validation. For now, generate potentially invalid rule & validate later.
            }
        }

        // For MONTHLY, we implicitly rely on the DTSTART day by default (no BYMONTHDAY/BYDAY needed for simple case)

        setDerivedRecurrenceRule(parts.join(';'));

    }, [isRecurring, frequency, interval, weekdays]);


    // Effect to set default weekday based on start time when switching to weekly/recurring
    useEffect(() => {
        if (isRecurring && frequency === 'WEEKLY' && startTimeObj) {
            // Only set default if *no* days are currently selected (avoid overriding user choice)
            const anyDaySelected = Object.values(weekdays).some(selected => selected);
            if (!anyDaySelected) {
                const startDay = dayjsToRruleWeekdays[startTimeObj.getDayOfWeek()];
                if (startDay) {
                    setWeekdays(prev => ({ ...prev, [startDay]: true }));
                }
            }
        }
        // If switching away from weekly, clear weekdays? Optional, maybe keep selection.
        // else if (!isRecurring || frequency !== 'WEEKLY') {
        //     setWeekdays({ MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false });
        // }

    }, [isRecurring, frequency, startTimeObj]); // Rerun if start time changes while weekly is active


    // --- Mutations ---
    const createPractitionerScheduleEntryMutation = trpc.practitioners.createScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry created." });
            utils.practitioners.getScheduleEntries.invalidate({ practitionerId: dataObjectId });
            utils.appointments.getAppointmentsForScheduler.invalidate();
            onSuccess(); // Close dialog etc.
        },
        onError: (err) => {
            console.error('Failed to create schedule entry:', err);
            toast.error("Error", { description: `Failed to create entry: ${err.message}` });
        },
    });

    const updatePractitionerScheduleEntryMutation = trpc.practitioners.updateScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry updated." });
            utils.practitioners.getScheduleEntries.invalidate({ practitionerId: dataObjectId });
            utils.appointments.getAppointmentsForScheduler.invalidate();
            onSuccess(); // Close dialog etc.
        },
        onError: (err) => {
            console.error('Failed to update schedule entry:', err);
            toast.error("Error", { description: `Failed to update entry: ${err.message}` });
        },
    });

    const deletePractitionerScheduleEntryMutation = trpc.practitioners.deleteScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry deleted." });
            utils.practitioners.getScheduleEntries.invalidate({ practitionerId: dataObjectId });
            utils.appointments.getAppointmentsForScheduler.invalidate();
            onSuccess();
        },
        onError: (err) => {
            console.error('Failed to delete schedule entry:', err);
            toast.error("Error", { description: `Failed to delete entry: ${err.message}` });
        },
    });

    const createEquipmentScheduleEntryMutation = trpc.equipment.createScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry created." });
            utils.appointments.getAppointmentsForScheduler.invalidate()
            onSuccess();
        },
        onError: (err) => {
            console.error('Failed to create schedule entry:', err);
            toast.error("Error", { description: `Failed to create entry: ${err.message}` });
        },
    });

    const updateEquipmentScheduleEntryMutation = trpc.equipment.updateScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry updated." });
            utils.appointments.getAppointmentsForScheduler.invalidate()
            onSuccess();
        },
        onError: (err) => {
            console.error('Failed to update schedule entry:', err);
            toast.error("Error", { description: `Failed to update entry: ${err.message}` });
        },
    });

    const deleteEquipmentScheduleEntryMutation = trpc.equipment.deleteScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Schedule entry deleted." });
            utils.appointments.getAppointmentsForScheduler.invalidate()
            onSuccess();
        },
        onError: (err) => {
            console.error('Failed to delete schedule entry:', err);
            toast.error("Error", { description: `Failed to delete entry: ${err.message}` });
        },
    });

    useEffect(() => {
        // Set selectedDataObjects to the original practitioner
        if (onlyUpdateForOriginalPractitioner === true) {
            if (dataObjectType === 'practitioner' && originalPractitionerData.data) {
                setSelectedDataObjects([originalPractitionerData.data]);
            }
        } else {
            // Back to default
            if (existingScheduleEntryData) {
                if (dataObjectType === 'practitioner') {
                    if ('practitionerScheduleEntryGroup' in existingScheduleEntryData && existingScheduleEntryData.practitionerScheduleEntryGroup && existingScheduleEntryData.practitionerScheduleEntryGroup.practitionerScheduleEntries.length > 0) {
                        setSelectedDataObjects(existingScheduleEntryData.practitionerScheduleEntryGroup?.practitionerScheduleEntries.map((entry) => entry.practitioner));
                    } else if ('practitioner' in existingScheduleEntryData) {
                        setSelectedDataObjects([existingScheduleEntryData.practitioner]);
                    }
                } else if (dataObjectType === 'equipment') {
                    if ('equipmentScheduleEntryGroup' in existingScheduleEntryData && existingScheduleEntryData.equipmentScheduleEntryGroup && existingScheduleEntryData.equipmentScheduleEntryGroup.equipmentScheduleEntries.length > 0) {
                        setSelectedDataObjects(existingScheduleEntryData.equipmentScheduleEntryGroup?.equipmentScheduleEntries.map((entry) => entry.equipment));
                    } else if ('equipment' in existingScheduleEntryData) {
                        setSelectedDataObjects([existingScheduleEntryData.equipment]);
                    }
                }
            }
        }
    }, [onlyUpdateForOriginalPractitioner]);

    const [dataSearchOpen, setDataSearchOpen] = useState(false);
    const [selectedDataObjects, setSelectedDataObjects] = useState<Practitioner[] | Equipment[]>([]);
    const [dataSearchQuery, setDataSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(dataSearchQuery, 300);

    const practitionerSearch = trpc.practitioners.search.useQuery(
        { query: debouncedSearchQuery, storeId: storeId, limit: 10 },
        {
            enabled: dataObjectType === 'practitioner' && dataSearchOpen, // Only fetch when query exists and popover is open
            staleTime: 30000, // Cache results briefly
        }
    );

    const equipmentSearch = trpc.equipment.search.useQuery(
        { query: debouncedSearchQuery, storeId: storeId, limit: 10 },
        {
            enabled: dataObjectType === 'equipment' && dataSearchOpen, // Only fetch when query exists and popover is open
            staleTime: 30000, // Cache results briefly
        }
    )

    const practitionerOptions = useMemo(() => practitionerSearch.data?.data || [], [practitionerSearch.data]);
    const equipmentOptions = useMemo(() => equipmentSearch.data?.data || [], [equipmentSearch.data]);

    const searchIsLoading = useMemo(() => {
        return ((dataObjectType === 'practitioner' && practitionerSearch.isLoading) || (dataObjectType === 'equipment' && equipmentSearch.isLoading));
    }, [dataObjectType, practitionerSearch, equipmentSearch]);

    const searchOptionsIsEmpty = useMemo(() => {
        return ((dataObjectType === 'practitioner' && !practitionerOptions.length) || (dataObjectType === 'equipment' && !equipmentOptions.length));
    }, [dataObjectType, practitionerOptions, equipmentOptions])


    // --- Handlers ---
    const handleDataObjectSelectAll = () => {
        if (dataObjectType === 'practitioner') {
            setSelectedDataObjects(practitionerOptions)
        } else if (dataObjectType === 'equipment') {
            setSelectedDataObjects(equipmentOptions)
        }
    }

    const handleDataObjectSelect = (dataObject: Practitioner | Equipment) => {
        const alreadySelected = selectedDataObjects.some((obj) => obj.id === dataObject.id);
        const isSingleSelectMode = (type as string) === 'AVAILABLE';
        if (alreadySelected) {
            const newSelectedDataObjects = selectedDataObjects.filter((obj) => obj.id !== dataObject.id) as Practitioner[] | Equipment[];
            setSelectedDataObjects(newSelectedDataObjects);
        } else if (isSingleSelectMode) {
            setSelectedDataObjects([dataObject] as Practitioner[] | Equipment[]);
        } else {
            const newSelectedDataObjects = [...selectedDataObjects, dataObject] as Practitioner[] | Equipment[];
            setSelectedDataObjects(newSelectedDataObjects);
        }
    };

    const dataTypeDisplayName = useMemo(() => {
        if (dataObjectType === 'practitioner') {
            return 'Behandler'
        } else if (dataObjectType === 'equipment') {
            return 'Gerät'
        }
        return 'N/A'
    }, [dataObjectType]);

    const dataObjectDisplayName = useMemo(() => {
        if (selectedDataObjects.length === 0) {
            return null;
        }
        if (dataObjectType === 'practitioner') {
            const practitioners = selectedDataObjects as Practitioner[];
            return practitioners.map((practitioner) => `${practitioner.title ? `${practitioner.title} ` : ""}${practitioner.firstName} ${practitioner.lastName}`).join(", ");
        } else if (dataObjectType === 'equipment') {
            const equipments = selectedDataObjects as Equipment[]
            return equipments.map((equipment) => equipment.name).join(", ");
        }
        return null
    }, [selectedDataObjects]);

    useEffect(() => {
        if (selectedDataObjects.length === 0 && dataObjectType === 'practitioner' && isEditing === false && !!dataObjectId && originalPractitionerData.data) {
            setSelectedDataObjects([originalPractitionerData.data]);
        }
    }, [originalPractitionerData, isEditing, dataObjectId]);

    useEffect(() => {
        if (existingScheduleEntryData) {
            if (dataObjectType === 'practitioner') {
                if ('practitionerScheduleEntryGroup' in existingScheduleEntryData && existingScheduleEntryData.practitionerScheduleEntryGroup && existingScheduleEntryData.practitionerScheduleEntryGroup.practitionerScheduleEntries.length > 0) {
                    setSelectedDataObjects(existingScheduleEntryData.practitionerScheduleEntryGroup?.practitionerScheduleEntries.map((entry) => entry.practitioner));
                } else if ('practitioner' in existingScheduleEntryData) {
                    setSelectedDataObjects([existingScheduleEntryData.practitioner]);
                }
            } else if (dataObjectType === 'equipment') {
                if ('equipmentScheduleEntryGroup' in existingScheduleEntryData && existingScheduleEntryData.equipmentScheduleEntryGroup && existingScheduleEntryData.equipmentScheduleEntryGroup.equipmentScheduleEntries.length > 0) {
                    setSelectedDataObjects(existingScheduleEntryData.equipmentScheduleEntryGroup?.equipmentScheduleEntries.map((entry) => entry.equipment));
                } else if ('equipment' in existingScheduleEntryData) {
                    setSelectedDataObjects([existingScheduleEntryData.equipment]);
                }
            }
        }
    }, [existingScheduleEntryData]);


    const mutationLoading = createPractitionerScheduleEntryMutation.isPending || createEquipmentScheduleEntryMutation.isPending || updatePractitionerScheduleEntryMutation.isPending || updateEquipmentScheduleEntryMutation.isPending;
    const deleteMutationLoading = deletePractitionerScheduleEntryMutation.isPending || deleteEquipmentScheduleEntryMutation.isPending;

    // --- Submission ---
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canManageShiftEntries) return;

        if (!startTimeObj || !endTimeObj) {
            toast.error("Error", { description: "Please provide valid start and end times." });
            return;
        }

        if (endTimeObj.lessThanOrEqual(startTimeObj)) {
            toast.error("Error", { description: "End time must be after start time." });
            return;
        }

        if (isRecurring && !derivedRecurrenceRule) {
            toast.error("Error", { description: "Recurrence rule (RRULE) is required for recurring entries." });
            return;
        }

        if (isEditing && isRecurring && scheduleEntryId) {
            setPendingFormData({ action: 'update' });
            setRecurrenceEditDialogIsDelete(false);
            setRecurrenceEditDialogOpen(true);
            return;
        }

        executeSubmit('all');
    };

    const executeSubmit = (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!startTimeObj || !endTimeObj) return;

        const selectedSlotDay = selectedSlot ? selectedSlot.start.format("yyyy-MM-dd") : null;

        let submitStartTime = startTimeObj;
        let submitEndTime = endTimeObj;

        if (editMode === 'all' && originalSeriesStart && originalSeriesEnd) {
            submitStartTime = originalSeriesStart.clone().setHours(startTimeObj.getHours()).setMinutes(startTimeObj.getMinutes());
            submitEndTime = originalSeriesEnd.clone().setHours(endTimeObj.getHours()).setMinutes(endTimeObj.getMinutes());
        }

        if (dataObjectType === 'practitioner') {
            const mutationData = {
                practitionerIds: selectedDataObjects.map((obj) => obj.id),
                storeId,
                type,
                blockedType,
                description: description || undefined,
                startTimeLocalSerialized: submitStartTime.serialize(),
                endTimeLocalSerialized: submitEndTime.serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring ? (recurrenceEndDateObj ? recurrenceEndDateObj.endOf('day').toISOString() : undefined) : undefined,
                detectedConflicts: serializeConflictsForLog(conflicts),
            };

            if (isEditing && scheduleEntryId) {
                updatePractitionerScheduleEntryMutation.mutate({
                    id: scheduleEntryId,
                    onlyUpdateForOriginalPractitioner: onlyUpdateForOriginalPractitioner,
                    originalPractitionerId: dataObjectId,
                    editMode,
                    selectedSlotDay,
                    ...mutationData,
                });
            } else {
                createPractitionerScheduleEntryMutation.mutate(mutationData);
            }
        } else if (dataObjectType === 'equipment') {
            const mutationData = {
                equipmentIds: selectedDataObjects.map((obj) => obj.id),
                type,
                description: description || undefined,
                startTime: submitStartTime.toUTC().toISOString(),
                endTime: submitEndTime.toUTC().toISOString(),
                startTimeLocalSerialized: submitStartTime.serialize(),
                endTimeLocalSerialized: submitEndTime.serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring ? (recurrenceEndDateObj ? recurrenceEndDateObj.endOf('day').toISOString() : undefined) : undefined,
            };

            if (isEditing && scheduleEntryId) {
                updateEquipmentScheduleEntryMutation.mutate({
                    id: scheduleEntryId,
                    onlyUpdateForOriginalEquipment: onlyUpdateForOriginalPractitioner,
                    originalEquipmentId: dataObjectId,
                    editMode,
                    selectedSlotDay,
                    ...mutationData,
                });
            } else {
                createEquipmentScheduleEntryMutation.mutate(mutationData);
            }
        }
    };

    const handleDelete = () => {
        if (!canManageShiftEntries) return;
        if (!scheduleEntryId) {
            return;
        }
        if (isRecurring) {
            setPendingFormData({ action: 'delete' });
            setRecurrenceEditDialogIsDelete(true);
            setRecurrenceEditDialogOpen(true);
            return;
        }
        executeDelete('all');
    };

    const executeDelete = (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!scheduleEntryId) return;

        const selectedSlotDay = selectedSlot ? selectedSlot.start.format("yyyy-MM-dd") : null;

        if (dataObjectType === 'practitioner') {
            deletePractitionerScheduleEntryMutation.mutate({
                id: scheduleEntryId,
                onlyDeleteForOriginalPractitioner: onlyUpdateForOriginalPractitioner,
                editMode,
                selectedSlotDay,
            });
        } else if (dataObjectType === 'equipment') {
            deleteEquipmentScheduleEntryMutation.mutate({
                id: scheduleEntryId,
                onlyDeleteForOriginalEquipment: onlyUpdateForOriginalPractitioner,
                editMode,
                selectedSlotDay,
            });
        }
    };

    const handleRecurrenceEditModeSelect = (mode: RecurrenceEditMode) => {
        setRecurrenceEditDialogOpen(false);
        if (pendingFormData?.action === 'delete') {
            executeDelete(mode);
        } else {
            executeSubmit(mode);
        }
        setPendingFormData(null);
    };


    const [conflicts, setConflicts] = useState<{
        hasConflicts: boolean;
        conflicts: Array<{
            appointmentId: string;
            appointmentTitle: string;
            customerName: string;
            practitionerId: number;
            practitionerName: string;
            stepStartSerialized: string;
            stepEndSerialized: string;
            blockerStartSerialized: string;
            blockerEndSerialized: string;
        }>;
        totalConflictsFound: number;
    } | null>(null);
    const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

    const checkConflicts = useCallback(async () => {
        if (dataObjectType !== 'practitioner' || type !== PractitionerScheduleEntryType.BLOCKED) {
            setConflicts(null);
            return;
        }

        if (!storeId || selectedDataObjects.length === 0 || !startTimeObj || !endTimeObj) {
            setConflicts(null);
            return;
        }

        if (!blockedType) {
            setConflicts(null);
            return;
        }

        setIsCheckingConflicts(true);
        try {
            const result = await utils.appointments.checkScheduleEntryConflicts.fetch({
                practitionerIds: (selectedDataObjects as Practitioner[]).map(p => p.id),
                storeId: storeId,
                startTimeLocalSerialized: startTimeObj.serialize(),
                endTimeLocalSerialized: endTimeObj.serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring && recurrenceEndDateObj ? recurrenceEndDateObj.toISOString() : undefined,
                scheduleEntryId: scheduleEntryId,
            });
            if (result.hasConflicts && 'totalConflictsFound' in result) {
                setConflicts(result);
            } else {
                setConflicts(null);
            }
        } catch (error) {
            console.error('Error checking conflicts:', error);
            setConflicts(null);
        } finally {
            setIsCheckingConflicts(false);
        }
    }, [dataObjectType, type, storeId, selectedDataObjects, startTimeObj, endTimeObj, blockedType, isRecurring, derivedRecurrenceRule, recurrenceEndDateObj, scheduleEntryId, utils]);

    const debouncedConflictCheck = useDebounceFunction(checkConflicts, 500);

    useEffect(() => {
        debouncedConflictCheck();
    }, [debouncedConflictCheck]);

    const [scheduleEntryLogsModalOpen, setScheduleEntryLogsModalOpen] = useState<boolean>(false);

    const [recurrenceEditDialogOpen, setRecurrenceEditDialogOpen] = useState(false);
    const [recurrenceEditDialogIsDelete, setRecurrenceEditDialogIsDelete] = useState(false);
    const [pendingFormData, setPendingFormData] = useState<Record<string, any> | null>(null);


    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">
                    Ausgewählt: <b>{onlyUpdateForOriginalPractitioner ? !!originalPractitionerData.data ? `${originalPractitionerData.data?.title ? `${originalPractitionerData.data?.title} ` : ""}${originalPractitionerData.data?.firstName} ${originalPractitionerData.data?.lastName}` : originalEquipmentData.data?.name : dataObjectDisplayName}</b>
                </span>

                {isEditing && isScheduleEntryInGroup && (
                    <div className="flex flex-row items-center gap-2 my-2">
                        <Switch
                            checked={onlyUpdateForOriginalPractitioner}
                            onCheckedChange={setOnlyUpdateForOriginalPractitioner}
                            disabled={!canManageShiftEntries}
                        />
                        <span className="text-sm">
                            Nur bearbeiten für: {`${!!originalPractitionerData.data ? `${originalPractitionerData.data?.title ? `${originalPractitionerData.data?.title} ` : ""}${originalPractitionerData.data?.firstName} ${originalPractitionerData.data?.lastName}` : originalEquipmentData.data?.name}`} (alle andere Ressourcen sind unberührt von dieser Aktion)
                        </span>
                    </div>
                )}
            </div>
            <div className="flex items-end gap-6">
                <div className="space-y-2">
                    <Label htmlFor="start-date">Datum *</Label>
                    <Input
                        id="start-date"
                        type="date"
                        className="w-[140px]"
                        value={formatDate(startTimeObj)}
                        onChange={(e) => {
                            setStartTimeObj(parseDate(e.target.value, startTimeObj, 'Europe/Berlin'));
                            setEndTimeObj(parseDate(e.target.value, endTimeObj, 'Europe/Berlin'));
                        }}
                        required
                        disabled={!canManageShiftEntries || mutationLoading}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="start-time">Startzeit *</Label>
                    <Input
                        id="start-time"
                        type="time"
                        className="w-[100px]"
                        value={formatTime(startTimeObj)}
                        onChange={(e) => setStartTimeObj(parseTime(e.target.value, startTimeObj, 'Europe/Berlin'))}
                        required
                        disabled={!canManageShiftEntries || mutationLoading}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="end-time">Endzeit *</Label>
                    <Input
                        id="end-time"
                        type="time"
                        className="w-[100px]"
                        value={formatTime(endTimeObj)}
                        onChange={(e) => setEndTimeObj(parseTime(e.target.value, endTimeObj, 'Europe/Berlin'))}
                        required
                        disabled={!canManageShiftEntries || mutationLoading}
                    />
                </div>
            </div>

            <div className="flex items-end gap-2">
                <div className="space-y-2">
                    <Label htmlFor="type">Typ</Label>
                    <Select
                        value={type}
                        onValueChange={(value) => setType(value as PractitionerScheduleEntryType | EquipmentScheduleEntryType)}
                        disabled={!canManageShiftEntries || mutationLoading || !!overwriteEntryType || isEditing}
                    >
                        <SelectTrigger id="type" className={type === PractitionerScheduleEntryType.AVAILABLE || type === EquipmentScheduleEntryType.AVAILABLE ? 'border-green-400' : 'border-red-400'}>
                            <SelectValue placeholder="Typ wählen" />
                        </SelectTrigger>
                        <SelectContent>
                            {dataObjectType === 'practitioner' ? (
                                <>
                                    {!!overwriteEntryType ? (
                                        <SelectItem value={overwriteEntryType}>{overwriteEntryType === PractitionerScheduleEntryType.AVAILABLE ? 'Verfügbarkeit' : 'Blocker'}</SelectItem>
                                    ) : (
                                        <>
                                            <SelectItem value={PractitionerScheduleEntryType.AVAILABLE}>Verfügbarkeit</SelectItem>
                                            <SelectItem value={PractitionerScheduleEntryType.BLOCKED}>Blocker</SelectItem>
                                        </>
                                    )}
                                </>
                            ) : null}
                            {dataObjectType === 'equipment' ? (
                                <>
                                    <SelectItem value={EquipmentScheduleEntryType.BLOCKED}>Blocker</SelectItem>
                                </>
                            ) : null}
                        </SelectContent>
                    </Select>
                </div>
                {dataObjectType === 'practitioner' && type === PractitionerScheduleEntryType.BLOCKED && (
                    <div className="space-y-2">
                        <Label htmlFor="blockedType">Blocker Grund</Label>
                        <Select
                            value={blockedType || undefined}
                            onValueChange={(value) => {
                                if (!!value) {
                                    setBlockedType(value as PractitionerScheduleEntryBlockedType)
                                }
                            }}
                            disabled={!canManageShiftEntries || mutationLoading}
                        >
                            <SelectTrigger id="blockedType">
                                <SelectValue placeholder="Blocker Grund wählen" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={PractitionerScheduleEntryBlockedType.BREAK}>Pause</SelectItem>
                                <SelectItem value={PractitionerScheduleEntryBlockedType.VACATION}>Urlaub</SelectItem>
                                <SelectItem value={PractitionerScheduleEntryBlockedType.MEETING}>Meeting</SelectItem>
                                <SelectItem value={PractitionerScheduleEntryBlockedType.SICK_LEAVE}>Krankheit</SelectItem>
                                <SelectItem value={PractitionerScheduleEntryBlockedType.EDUCATION}>Fortbildung</SelectItem>
                                <SelectItem value={PractitionerScheduleEntryBlockedType.OTHER}>Sonstiges</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="description">Beschreibung (optional)</Label>
                <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="z.B. Pause, Teammeeting"
                    disabled={!canManageShiftEntries || mutationLoading}
                />
            </div>

            {/* Is Recurring Switch */}
            <div className="flex items-center gap-2">
                <Switch
                    id="is-recurring"
                    checked={isRecurring}
                    onCheckedChange={(checked) => setIsRecurring(Boolean(checked))}
                    disabled={!canManageShiftEntries || mutationLoading || isEditing}
                />
                <Label htmlFor="is-recurring" className="cursor-pointer">Wiederkehrend</Label>
            </div>

            {isRecurring && (
                <div className="space-y-4 border rounded-md p-3 bg-muted/30">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Frequenz</Label>
                            <Select
                                value={frequency}
                                onValueChange={(value: 'DAILY' | 'WEEKLY' | 'MONTHLY') => setFrequency(value)}
                                disabled={!canManageShiftEntries || mutationLoading || isEditing}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='DAILY'>Täglich</SelectItem>
                                    <SelectItem value='WEEKLY'>Wöchentlich</SelectItem>
                                    <SelectItem value='MONTHLY'>Monatlich</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Intervall (alle x Tage/Wochen/Monate)</Label>
                            <Input
                                type="number"
                                min={1}
                                value={interval}
                                onChange={(e) => setInterval(parseInt(e.target.value, 10) || 1)}
                                disabled={!canManageShiftEntries || mutationLoading}
                            />
                        </div>
                    </div>

                    {frequency === 'WEEKLY' && (
                        <div className="space-y-2">
                            <Label>Wochentage</Label>
                            <div className="flex gap-2 flex-wrap">
                                {Object.entries(weekdays).map(([day, isSelected]) => (
                                    <label key={day} className="flex items-center gap-1 text-sm">
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked) =>
                                                setWeekdays(prev => ({ ...prev, [day]: Boolean(checked) }))
                                            }
                                            disabled={!canManageShiftEntries || mutationLoading}
                                        />
                                        {{ MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So' }[day]}
                                    </label>
                                ))}
                            </div>
                            {!Object.values(weekdays).some(selected => selected) && (
                                <p className="text-xs text-destructive">Bitte mindestens einen Tag auswählen.</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Enddatum</Label>
                        <Input
                            type="date"
                            value={recurrenceEndDateObj?.format('YYYY-MM-DD') ?? ''}
                            onChange={(e) => setRecurrenceEndDateObj(e.target.value ? dayjs(e.target.value).tz(userTimezone, true) : null)}
                            disabled={!canManageShiftEntries || mutationLoading}
                        />
                    </div>
                </div>
            )}


            <div className='flex flex-col gap-4'>
                {dataObjectType === 'practitioner' && (
                    <>
                        {conflicts && conflicts.hasConflicts ? (
                            <div className="space-y-2">
                                <Alert variant="destructive">
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertTitle>Kalenderkonflikte gefunden!</AlertTitle>
                                    <AlertDescription>
                                        <p className="mb-2">
                                            {conflicts.totalConflictsFound > conflicts.conflicts.length
                                                ? `${conflicts.totalConflictsFound} Konflikte gefunden (erste ${conflicts.conflicts.length} angezeigt):`
                                                : `${conflicts.conflicts.length} Konflikt(e) gefunden:`}
                                        </p>
                                        <ul className="list-disc pl-5 space-y-1 text-sm max-h-[200px] overflow-y-auto">
                                            {conflicts.conflicts.map((conflict, idx) => (
                                                <li key={idx}>
                                                    <strong>{conflict.practitionerName}</strong>: Termin "{conflict.appointmentTitle}"
                                                    ({conflict.customerName}) am {LocalDateTime.deserialize(conflict.stepStartSerialized).format('dd.MM.yyyy HH:mm')}{' '}
                                                    überschneidet sich mit Blockierung ({LocalDateTime.deserialize(conflict.blockerStartSerialized).format('dd.MM.yyyy HH:mm')} - {LocalDateTime.deserialize(conflict.blockerEndSerialized).format('dd.MM.yyyy HH:mm')})
                                                </li>
                                            ))}
                                        </ul>
                                    </AlertDescription>
                                </Alert>
                            </div>
                        ) : !isCheckingConflicts ? (
                            <Alert className="border-green-500 bg-green-50 text-green-900">
                                <Check className="h-4 w-4 text-green-600" />
                                <AlertTitle className="text-green-900">Keine Konflikte erkannt</AlertTitle>
                                <AlertDescription className="text-green-800">
                                    Keine Kalenderkonflikte erkannt.
                                </AlertDescription>
                            </Alert>
                        ) : null}
                    </>
                )}

                <div className='flex justify-between mt-16'>
                    {canManageShiftEntries && scheduleEntryId ? (
                        <Button type="button" variant="destructive" onClick={handleDelete} disabled={mutationLoading || deleteMutationLoading}>
                            Löschen {deleteMutationLoading && <Loader2 className='animate-spin' />}
                        </Button>
                    ) : <div />}
                    <div className="flex justify-end space-x-3">
                        <Button type="button" variant="outline" onClick={onCancel} disabled={mutationLoading || deleteMutationLoading}>
                            Abbrechen
                        </Button>
                        {canManageShiftEntries && (
                            <Button type="submit" disabled={mutationLoading || deleteMutationLoading || selectedDataObjects.length === 0 || (dataObjectType === 'practitioner' && type === PractitionerScheduleEntryType.BLOCKED && !blockedType)}>
                                {mutationLoading ? 'Speichern...' : (isEditing ? 'Speichern' : 'Add Entry')}
                                {mutationLoading && <Loader2 className='animate-spin' />}
                            </Button>
                        )}
                    </div>
                </div>

                {!!scheduleEntryId && (
                    <>
                        <ScheduleEntryLogsModal
                            scheduleEntryId={scheduleEntryId}
                            open={scheduleEntryLogsModalOpen}
                            onClose={() => setScheduleEntryLogsModalOpen(false)}
                        />
                        <div className="flex items-center gap-2 mt-4">
                            <span className='text-xs text-gray-700'>Schedule Entry ID: {scheduleEntryId}</span>
                            <Button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setScheduleEntryLogsModalOpen(true)
                                }}
                                variant='ghost'
                                size="sm"
                            >
                                <Book className="h-4 w-4 mr-2" />
                                View logs
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <RecurrenceEditModeDialog
                open={recurrenceEditDialogOpen}
                isDelete={recurrenceEditDialogIsDelete}
                onSelect={handleRecurrenceEditModeSelect}
                onCancel={() => {
                    setRecurrenceEditDialogOpen(false);
                    setPendingFormData(null);
                }}
                occurrenceLabel={startTimeObj?.format('EEEE, dd.MM.yyyy')}
                occurrenceDate={startTimeObj?.format('dd.MM.yyyy')}
                seriesStartDate={originalSeriesStart?.format('dd.MM.yyyy')}
                seriesEndDate={recurrenceEndDateObj?.format('DD.MM.YYYY')}
            />
        </form>
    );
};